/**
 * A curated icon set, drawn here rather than imported.
 *
 * Owned outright: no licence to track, nothing fetched at runtime, and one consistent hand
 * across every subject. All are 24×24, stroked at 1.75, and inherit `currentColor`, so an
 * icon takes the colour of whatever it sits in and never needs a second palette.
 *
 * The set is deliberately small. Forty icons a teacher recognises beat four hundred nobody
 * can tell apart, and an icon that has to be explained is worse than no icon.
 */

import type { ReactNode } from 'react';

const P = (d: string) => <path d={d} />;

/** Keyed by name. Each value is the inner geometry of a 24×24 stroked icon. */
export const ICONS: Record<string, ReactNode> = {
  // ---- science
  beaker: <>{P('M9 3h6M10 3v6l-5 9a2 2 0 0 0 1.8 3h10.4a2 2 0 0 0 1.8-3l-5-9V3')}{P('M7.5 15h9')}</>,
  atom: <><circle cx="12" cy="12" r="2.2" /><ellipse cx="12" cy="12" rx="10" ry="4.4" /><ellipse cx="12" cy="12" rx="10" ry="4.4" transform="rotate(60 12 12)" /><ellipse cx="12" cy="12" rx="10" ry="4.4" transform="rotate(120 12 12)" /></>,
  dna: <>{P('M7 3c0 5 10 5 10 10S7 18 7 21')}{P('M17 3c0 5-10 5-10 10s10 5 10 8')}{P('M8.5 7h7M8.5 17h7M7.5 12h9')}</>,
  leaf: <>{P('M4 20C4 11 11 4 20 4c0 9-7 16-16 16Z')}{P('M4 20 14 10')}</>,
  magnet: <>{P('M6 4v8a6 6 0 0 0 12 0V4')}{P('M6 4h4v8a2 2 0 0 0 4 0V4h4')}</>,
  thermometer: <>{P('M12 3a2.5 2.5 0 0 1 2.5 2.5v8.2a4.5 4.5 0 1 1-5 0V5.5A2.5 2.5 0 0 1 12 3Z')}{P('M12 8v7')}</>,

  // ---- maths
  chart: <>{P('M4 20V4M4 20h16')}{P('M8 16v-4M12 16V8M16 16v-6')}</>,
  trend: <>{P('M4 17 10 11l4 4 6-7')}{P('M15 8h5v5')}</>,
  sigma: <>{P('M17 5H7l6 7-6 7h10')}</>,
  compass: <><circle cx="12" cy="12" r="9" />{P('m15.5 8.5-2 5.5-5.5 2 2-5.5Z')}</>,
  ruler: <>{P('M3 15 15 3l6 6L9 21Z')}{P('M7 11l2 2M10 8l2 2M13 5l2 2')}</>,
  percent: <><circle cx="7.5" cy="7.5" r="2.5" /><circle cx="16.5" cy="16.5" r="2.5" />{P('m19 5-14 14')}</>,

  // ---- humanities
  globe: <><circle cx="12" cy="12" r="9" />{P('M3 12h18')}<ellipse cx="12" cy="12" rx="4" ry="9" /></>,
  map: <>{P('m3 6 6-3 6 3 6-3v15l-6 3-6-3-6 3Z')}{P('M9 3v15M15 6v15')}</>,
  scales: <>{P('M12 4v16M7 20h10')}{P('M4 8h16')}{P('M4 8 1.5 14a3 3 0 0 0 5 0Z')}{P('M20 8l2.5 6a3 3 0 0 1-5 0Z')}</>,
  landmark: <>{P('M3 21h18M5 21V10m4 11V10m6 11V10m4 11V10')}{P('m2.5 10 9.5-6 9.5 6Z')}</>,
  clock: <><circle cx="12" cy="12" r="9" />{P('M12 7v5l3.5 2')}</>,
  quote: <>{P('M9 6C6 7.5 4.5 10 4.5 13H8v5H3v-5c0-5 2-8.5 6-10Z')}{P('M20 6c-3 1.5-4.5 4-4.5 7H19v5h-5v-5c0-5 2-8.5 6-10Z')}</>,

  // ---- language & arts
  book: <>{P('M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2Z')}{P('M8 3v14')}</>,
  pen: <>{P('M4 20h4L20 8a2.8 2.8 0 0 0-4-4L4 16Z')}{P('m14.5 5.5 4 4')}</>,
  speak: <>{P('M4 5h16v11H9l-5 4Z')}{P('M8 9h8M8 12.5h5')}</>,
  palette: <>{P('M12 3a9 9 0 1 0 0 18c1.4 0 2-.9 2-1.8 0-1.4-1.2-1.8-1.2-3 0-.8.7-1.4 1.6-1.4H16a5 5 0 0 0 5-5c0-3.8-4-6.8-9-6.8Z')}<circle cx="7.5" cy="12" r="1" /><circle cx="10" cy="8" r="1" /><circle cx="15" cy="8.5" r="1" /></>,
  music: <>{P('M9 18V5l10-2v13')}<circle cx="6.5" cy="18" r="2.5" /><circle cx="16.5" cy="16" r="2.5" /></>,

  // ---- teaching moves
  idea: <>{P('M9.5 18h5M10 21h4')}{P('M12 3a6 6 0 0 1 3.6 10.8c-.6.5-.9 1-1 1.7l-.1.5h-5l-.1-.5c-.1-.7-.4-1.2-1-1.7A6 6 0 0 1 12 3Z')}</>,
  question: <><circle cx="12" cy="12" r="9" />{P('M9.4 9.2a2.7 2.7 0 0 1 5.2.9c0 1.8-2.6 2.2-2.6 3.9')}{P('M12 17.4h.01')}</>,
  target: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.4" /></>,
  check: <><circle cx="12" cy="12" r="9" />{P('m8 12.5 2.7 2.7L16 9.8')}</>,
  warning: <>{P('M12 3.5 22 20H2Z')}{P('M12 10v4.5M12 17.4h.01')}</>,
  people: <><circle cx="9" cy="8" r="3.2" />{P('M3 20a6 6 0 0 1 12 0')}{P('M16 5.2a3.2 3.2 0 0 1 0 5.6M17 14.4A6 6 0 0 1 21 20')}</>,
  timer: <><circle cx="12" cy="13.5" r="7.5" />{P('M12 9.5v4M9.5 2h5M18.5 7 20 5.5')}</>,
  gear: <><circle cx="12" cy="12" r="3" />{P('M12 2v3M12 19v3M22 12h-3M5 12H2M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1M18.4 18.4l-2.1-2.1M7.7 7.7 5.6 5.6')}</>,
  link: <>{P('M10 13.5a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1.5 1.5')}{P('M14 10.5a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1.5-1.5')}</>,
  layers: <>{P('m12 3 9 5-9 5-9-5Z')}{P('m3 13 9 5 9-5')}{P('m3 17 9 5 9-5')}</>,
  code: <>{P('m9 8-5 4 5 4M15 8l5 4-5 4')}</>,
  cycle: <>{P('M4 12a8 8 0 0 1 13.7-5.6L20 8')}{P('M20 4v4h-4')}{P('M20 12a8 8 0 0 1-13.7 5.6L4 16')}{P('M4 20v-4h4')}</>,
};

export type IconName = keyof typeof ICONS;

/**
 * Which icon a phrase deserves, or none.
 *
 * Keyword-matched rather than model-chosen: a generator asked to name an icon will invent
 * names that are not in the set, and an icon that silently falls back to a generic one is
 * worse than a card with no icon at all.
 */
const KEYWORDS: [RegExp, IconName][] = [
  [/\b(price|cost|income|demand|supply|market|econom|inflat|gdp|profit|trade)/i, 'trend'],
  [/\b(graph|chart|data|statistic|plot|axis)\b/i, 'chart'],
  [/\b(sum|total|average|mean|calculat|equation|algebra)\b/i, 'sigma'],
  [/\b(percent|proportion|ratio|rate)\b/i, 'percent'],
  [/\b(measure|length|scale|unit|dimension)\b/i, 'ruler'],
  [/\b(angle|geometry|construct|direction|bearing)\b/i, 'compass'],
  [/\b(experiment|lab|reaction|solution|acid|chemical)\b/i, 'beaker'],
  [/\b(atom|particle|molecul|element|electron|bond)\b/i, 'atom'],
  [/\b(cell|gene|dna|organism|inherit|evolution)\b/i, 'dna'],
  [/\b(plant|environment|ecosystem|carbon|sustainab|climate)\b/i, 'leaf'],
  [/\b(force|magnet|field|charge|current)\b/i, 'magnet'],
  [/\b(temperature|heat|thermal|energy)\b/i, 'thermometer'],
  [/\b(world|global|international|country|nation)\b/i, 'globe'],
  [/\b(map|region|place|territory|location|landscape)\b/i, 'map'],
  [/\b(justice|law|fair|right|ethic|balance|equit)\b/i, 'scales'],
  [/\b(history|ancient|civilisation|civilization|empire|government|state)\b/i, 'landmark'],
  [/\b(time|period|century|year|date|chronolog)\b/i, 'clock'],
  [/\b(quote|source|text|passage|extract)\b/i, 'quote'],
  [/\b(read|book|literature|novel|story|chapter)\b/i, 'book'],
  [/\b(write|essay|draft|compose|author)\b/i, 'pen'],
  [/\b(speak|discuss|debate|argue|present|oral)\b/i, 'speak'],
  [/\b(art|colour|color|design|aesthetic|visual)\b/i, 'palette'],
  [/\b(music|rhythm|sound|note|perform)\b/i, 'music'],
  [/\b(idea|concept|think|understand|insight|hypoth|taste|prefer|expect|belief)/i, 'idea'],
  [/\b(why|how|what|question|inquir|wonder)\b/i, 'question'],
  [/\b(aim|goal|objective|outcome|purpose)\b/i, 'target'],
  [/\b(check|correct|verify|confirm|assess)\b/i, 'check'],
  [/\b(risk|caution|careful|mistake|error|misconcept)\b/i, 'warning'],
  [/\b(group|team|class|student|people|societ|communit|buyer|seller|consumer|popul)/i, 'people'],
  [/\b(minute|duration|timing|quick|pace)\b/i, 'timer'],
  [/\b(method|process|system|mechanism|procedure)\b/i, 'gear'],
  [/\b(connect|relation|link|between|depend|substitut|complement)/i, 'link'],
  [/\b(layer|structure|level|hierarch|stage)\b/i, 'layers'],
  [/\b(code|program|algorithm|function|loop)\b/i, 'code'],
  [/\b(cycle|repeat|loop|return|feedback|circular)\b/i, 'cycle'],
];

export function iconFor(text: string): IconName | null {
  for (const [re, name] of KEYWORDS) if (re.test(text)) return name;
  return null;
}

/**
 * Icons for a set, or icons for none of it.
 *
 * A card grid where five of seven carry an icon and two carry a gap does not read as
 * "we had nothing for those two" — it reads as broken. So the set is all-or-nothing, and the
 * bar is every item. Half-iconography is worse than none.
 */
export function iconsForSet(items: string[]): (IconName | null)[] | null {
  const found = items.map(iconFor);
  return found.every(Boolean) ? found : null;
}

export function Icon({ name, size = 24 }: { name: IconName; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {ICONS[name]}
    </svg>
  );
}
